import React from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import FAQSection from './FAQSection';
import './FinanceAccountingPage.css';

const FinanceAccountingPage = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  const [featuresRef, featuresInView] = useInView({ threshold: 0.1 });
  const [benefitsRef, benefitsInView] = useInView({ threshold: 0.1 });
  const [processRef, processInView] = useInView({ threshold: 0.1 });

  const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const keyFeatures = [
    {
      icon: "bi-calculator",
      title: "Automated Financial Reporting",
      description: "Generate comprehensive financial statements, P&L reports, and compliance documents automatically.",
      color: "#FF7A00"
    },
    {
      icon: "bi-shield-check",
      title: "Compliance Management",
      description: "Stay compliant with tax regulations, audit requirements, and industry standards effortlessly.",
      color: "#0066CC"
    },
    {
      icon: "bi-graph-up-arrow",
      title: "Real-time Financial Analytics",
      description: "Access live dashboards with KPIs, cash flow analysis, and performance metrics for informed decisions.",
      color: "#FF7A00"
    },
    {
      icon: "bi-credit-card",
      title: "Accounts Receivable & Payable",
      description: "Streamline invoicing, payment processing, and vendor management with automated workflows.",
      color: "#0066CC"
    },
    {
      icon: "bi-building",
      title: "Multi-entity Management",
      description: "Manage multiple business entities with consolidated reporting and inter-company transactions.",
      color: "#FF7A00"
    },
    {
      icon: "bi-people-fill",
      title: "Client Relationship Management",
      description: "Track client interactions, project profitability, and maintain strong customer relationships.",
      color: "#0066CC"
    }
  ];

  const benefits = [
    {
      icon: "bi-graph-up-arrow",
      title: "Improved Accuracy",
      description: "Eliminate manual errors and ensure accurate financial data with automated processes.",
      stats: "99% accuracy improvement"
    },
    {
      icon: "bi-clock",
      title: "Time Savings",
      description: "Reduce time spent on manual tasks and focus on strategic financial planning.",
      stats: "60% time reduction"
    },
    {
      icon: "bi-shield-check",
      title: "Enhanced Compliance",
      description: "Maintain regulatory compliance with built-in controls and audit trails.",
      stats: "100% compliance rate"
    },
    {
      icon: "bi-currency-dollar",
      title: "Cost Optimization",
      description: "Reduce operational costs through process automation and improved efficiency.",
      stats: "30% cost reduction"
    },
    {
      icon: "bi-people",
      title: "Better Client Service",
      description: "Provide faster, more accurate service to clients with real-time data access.",
      stats: "40% faster response"
    },
    {
      icon: "bi-piggy-bank",
      title: "Increased Profitability",
      description: "Maximize profits through better financial visibility and strategic insights.",
      stats: "25% profit increase"
    }
  ];

  const processSteps = [
    {
      step: "01",
      title: "Assessment & Planning",
      description: "Analyze your current financial processes and design a customized NetSuite solution.",
      icon: "bi-search"
    },
    {
      step: "02",
      title: "System Configuration",
      description: "Configure NetSuite to match your accounting workflows and compliance requirements.",
      icon: "bi-gear"
    },
    {
      step: "03",
      title: "Data Migration",
      description: "Securely migrate your historical financial data and client information to NetSuite.",
      icon: "bi-arrow-up-circle"
    },
    {
      step: "04",
      title: "Training & Go-Live",
      description: "Train your team and provide ongoing support for a smooth transition to NetSuite.",
      icon: "bi-rocket-takeoff"
    }
  ];

  return (
    <div className="finance-accounting-page">
      <Helmet>
        <title>NetSuite for Finance & Accounting USA | SpireNest</title>
        <link rel="canonical" href="https://spirenest.co/netsuite/industries/finance-accounting" />
        <meta name="description" content="Optimize financial management with SpireNest's NetSuite ERP services, tailored for U.S. finance and accounting professionals." />
        <meta name="keywords" content="NetSuite finance, accounting ERP, financial management, compliance automation, USA, SpireNest" />
        <meta property="og:title" content="NetSuite for Finance & Accounting Firms | SpireNest USA" />
        <meta property="og:description" content="Automate accounting, reporting, and compliance with NetSuite ERP for financial firms." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://spirenest.co/industries/finance-accounting" />
        <meta property="og:image" content="https://spirenest.co/assets/images/og-finance.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="NetSuite for Finance & Accounting Firms | SpireNest USA" />
        <meta name="twitter:description" content="Modern ERP solutions to streamline financial operations and boost accuracy." />
        <meta name="twitter:image" content="https://spirenest.co/assets/images/og-finance.jpg" />
      </Helmet>

      {/* Hero Section */}
      <section className="hero-section position-relative overflow-hidden">
        <div className="hero-bg-overlay"></div>
        <div className="floating-shapes">
          <motion.div className="shape-1" animate={{ rotate: 360, y: [-20, 20] }} transition={{ duration: 20, repeat: Infinity }} />
          <motion.div className="shape-2" animate={{ rotate: -360, x: [-30, 30] }} transition={{ duration: 25, repeat: Infinity }} />
          <motion.div className="shape-3" animate={{ y: [-40, 40], x: [-20, 20] }} transition={{ duration: 30, repeat: Infinity }} />
        </div>

        <Container className="position-relative">
          <Row className="min-vh-100 align-items-center">
            <Col lg={6}>
              <motion.div
                ref={heroRef}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                variants={fadeInUp}
                className="hero-content"
              >
                <Badge bg="primary" className="hero-badge mb-3">
                  <i className="bi bi-calculator me-2"></i>
                  NetSuite for Finance Industry
                </Badge>
                <h1 className="hero-title mb-4">
                  Transform Your <span style={{background: 'linear-gradient(135deg, #FF8F00, #FF6B35)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent'}}>Financial Operations</span> with NetSuite ERP
                </h1>
                <p className="hero-description mb-4">
                  Streamline accounting processes, ensure compliance, and gain real-time financial insights
                  with NetSuite's comprehensive ERP solution designed specifically for finance and accounting firms.
                </p>
                
              </motion.div>
            </Col>
            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={heroInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
                transition={{ duration: 1, ease: "easeOut" }}
                className="hero-image-container"
              >
                <div className="hero-image-wrapper">
                  
                  <div className="floating-card card-2">
                    <i className="bi bi-shield-check text-primary"></i>
                    <span>Compliance Ready</span>
                  </div>
                  <div className="floating-card card-3">
                    <i className="bi bi-clock text-warning"></i>
                    <span>Real-time Reporting</span>
                  </div>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Key Features Section */}
      <section className="features-section py-5 bg-light">
        <Container>
          <motion.div
            ref={featuresRef}
            initial="hidden"
            animate={featuresInView ? "visible" : "hidden"}
            variants={staggerContainer}
            className="text-center mb-5"
          >
            <motion.h2 variants={fadeInUp} className="section-title mb-3">
              Core Features of <span className="text-primary">NetSuite Financial Management</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="section-subtitle text-muted">
              Comprehensive financial tools designed to streamline your accounting operations and enhance decision-making
            </motion.p>
          </motion.div>

          <Row>
            {keyFeatures.map((feature, index) => (
              <Col lg={4} md={6} className="mb-4" key={index}>
                <motion.div
                  initial="hidden"
                  animate={featuresInView ? "visible" : "hidden"}
                  variants={fadeInUp}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="feature-card h-100 border-0 shadow-sm">
                    <Card.Body className="p-4 text-center">
                      <div
                        className="feature-icon-wrapper mb-3"
                        style={{ backgroundColor: `${feature.color}15` }}
                      >
                        <i
                          className={`${feature.icon} feature-icon`}
                          style={{ color: feature.color }}
                        ></i>
                      </div>
                      <h5 className="feature-title mb-3">{feature.title}</h5>
                      <p className="feature-description text-muted">{feature.description}</p>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section py-5">
        <Container>
          <motion.div
            ref={benefitsRef}
            initial="hidden"
            animate={benefitsInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <Row className="align-items-center mb-5">
              <Col lg={12}>
                <motion.div variants={slideInLeft} className="text-center">
                  <h2 className="section-title mb-3">
                    Benefits of <span className="text-primary">NetSuite Financial Management</span>
                  </h2>
                  <p className="section-subtitle text-muted mb-4">
                    Discover how NetSuite transforms financial operations for accounting firms and finance teams
                  </p>
                  <Button
                    as={Link}
                    to="/contact"
                    size="lg"
                    className="custom-btn-primary"
                  >
                    Learn More
                    <i className="bi bi-arrow-right ms-2"></i>
                  </Button>
                </motion.div>
              </Col>
            </Row>

            <Row>
              {benefits.map((benefit, index) => (
                <Col lg={4} md={6} className="mb-4" key={index}>
                  <motion.div
                    variants={fadeInUp}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="benefit-card h-100 border-0 shadow-sm">
                      <Card.Body className="p-4">
                        <div className="d-flex align-items-start mb-3">
                          <div className="benefit-icon-wrapper me-3">
                            <i className={`${benefit.icon} benefit-icon`}></i>
                          </div>
                          <div>
                            <h5 className="benefit-title  mt-3">{benefit.title}</h5>
                            {/* <Badge bg="primary" className="mb-2">{benefit.stats}</Badge> */}
                          </div>
                        </div>
                        <p className="benefit-description text-muted mb-0">{benefit.description}</p>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Implementation Process Section */}
      <section className="process-section py-5 bg-light">
        <Container>
          <motion.div
            ref={processRef}
            initial="hidden"
            animate={processInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <div className="text-center mb-5">
              <motion.h2 variants={fadeInUp} className="section-title mb-3">
                Our <span className="text-primary">Implementation Process</span>
              </motion.h2>
              <motion.p variants={fadeInUp} className="section-subtitle text-muted">
                A proven methodology to ensure successful NetSuite implementation for your finance operations
              </motion.p>
            </div>

            <Row>
              {processSteps.map((step, index) => (
                <Col lg={3} md={6} className="mb-4 d-flex" key={index}>
                  <motion.div
                    variants={fadeInUp}
                    transition={{ delay: index * 0.2 }}
                    className="w-100"
                  >
                    <Card className="process-card h-100 text-center border-0 shadow-sm">
                      <Card.Body className="p-4">
                        <div className="process-step-number mb-3">{step.step}</div>
                        <div className="process-icon-wrapper mb-5">
                          <i className={`${step.icon} process-icon`}></i>
                        </div>
                        <h5 className="process-title mb-3">{step.title}</h5>
                        <p className="process-description text-muted">{step.description}</p>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5 bg-primary text-white">
        <Container>
          <Row className="align-items-center">
            <Col lg={8}>
              <h2 className="cta-title mb-3">Ready to Transform Your Financial Operations?</h2>
              <p className="cta-description mb-0">
                Partner with Spirenest to implement NetSuite and revolutionize your finance and accounting processes.
              </p>
            </Col>
            <Col lg={4} className="text-lg-end">
              <Button
                as={Link}
                to="/contact"
                size="lg"
                variant="light"
                className="custom-btn-light"
              >
                Get Started Today
                <i className="bi bi-arrow-right ms-2"></i>
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      <FAQSection />
    </div>
  );
};

export default FinanceAccountingPage;